/**
 * What a watch is on the wire, and how its state reads on the list.
 *
 * Split out of `WatchesPage` so the status rule is testable on its own — and
 * because a page module that a lazy route imports may only export components.
 */
import { truncateAddress } from "@authe/fe-commons/wallet";

export interface WatchItem {
  id: string;
  /** Human label for the watch. */
  name?: string;
  description?: string;
  /** The watched address, transaction hash, or situation id. */
  target?: string;
  /**
   * Blockchain the target lives on.
   *
   * The API sends `chain_ids`; `chain` is only ever set by a caller that built
   * a watch locally. Read both through `watchChain` rather than either directly.
   */
  chain?: string;
  /** CAIP-2 ids the watch is scoped to. Empty means every chain. */
  chain_ids?: string[];
  /**
   * A pipeline definition this watch is bound to, or absent for every pipeline.
   *
   * Set it when the watch depends on a decode path only that pipeline runs; the
   * same transaction through another pipeline produces a shape the watch cannot
   * match, so firing on it would be a false positive.
   */
  pipeline_id?: string | null;
  enabled?: boolean;
  /** `address` | `transaction` | `situation` | a catalog kind. */
  kind?: string;
  severity?: string;
  /** Situation watches only: the params the catalog entry declared. */
  params?: Record<string, unknown>;
  /** The compiled rule. The alert history is keyed on it, not on the watch. */
  trigger_rule_id?: string;
  /** Times this watch's rule has fired, maintained by the pipeline. */
  hit_count?: number;
  /** Unix seconds of the most recent firing. */
  last_hit_at?: number | null;
  created_at?: number;
  /**
   * Situation watches only: whether the platform actually detects this
   * situation on this watch's chains. Stamped on read by trigger-mgmt-api.
   */
  coverage?: "available" | "unavailable" | "unknown";
  /**
   * Why coverage is `unavailable`. Three different fixes hide behind one word,
   * so the detail view says which — see `coverageExplanation`.
   */
  coverage_reason?: string | null;
  /**
   * Who supplies the coverage when it is `available`.
   *
   * `system` is the platform sweep, which runs continuously. `tenant` is one of
   * your own pipelines, which runs when you start a run — the same word
   * "covered", a materially weaker promise, and flattening the two would have
   * this page promise live alerting a `pl_*` pipeline does not give.
   */
  covered_by?: "system" | "tenant" | null;
}

export type WatchListPayload = unknown[] | { items?: unknown[] };

/**
 * The list's rows, checked rather than cast.
 *
 * Every field but `id` is optional and every reader here is already defensive,
 * so this does not hand-copy twenty fields — that coercer would drift behind
 * the interface above within a release. What it does enforce is the one field
 * the page cannot work without: `id` keys the row, opens the editor and
 * addresses the DELETE. A row without one renders as a watch nobody can edit or
 * remove, so it is dropped here rather than drawn as a dead entry.
 */
export function normalizeWatches(payload: WatchListPayload | undefined): WatchItem[] {
  if (!payload) return [];
  const rows = Array.isArray(payload) ? payload : (payload.items ?? []);
  return rows.filter(
    (row): row is WatchItem =>
      typeof row === "object" &&
      row !== null &&
      typeof (row as { id?: unknown }).id === "string" &&
      (row as { id: string }).id !== "",
  );
}

/**
 * A catalog id read as English: `uniswap.pool_opened` → "Uniswap · Pool opened".
 *
 * The dialog holds the catalog and its authored labels; a row does not, and
 * fetching the catalog to draw a list would spend a round-trip on a caption.
 * The ids are namespaced and snake-cased by convention, so unpicking the
 * separators gets close enough to the authored label to read as one.
 */
function situationTitle(id: string): string {
  const sentence = (part: string): string =>
    part.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  const dot = id.indexOf(".");
  if (dot <= 0) return sentence(id);
  const leaf = id.slice(dot + 1).replace(/\./g, " ");
  return `${sentence(id.slice(0, dot))} · ${sentence(leaf)}`;
}

/**
 * What to call a watch in a heading, a row, or a confirm.
 *
 * `name` is optional on the wire and the dialog does not press for one, so most
 * watches arrive without it and the FALLBACK is what a user actually reads. It
 * has to describe the watch: a list of identical "Untitled watch" rows is worse
 * than no caption, and the watch always knows what it watches.
 */
export function watchTitle(watch: WatchItem): string {
  const name = watch.name?.trim();
  if (name) return name;
  const target = watch.target?.trim();
  if (!target) return "Untitled watch";
  if (watch.kind === "situation") return situationTitle(target);
  if (watch.kind === "transaction") return `Transaction ${truncateAddress(target)}`;
  return `Address ${truncateAddress(target)}`;
}

export interface WatchStatus {
  label: string;
  variant: "default" | "secondary" | "outline";
}

/**
 * The badge a watch's state deserves.
 *
 * "Active" and "Paused" were enough while every watch was a subject watch,
 * because a subject either has activity or does not. A situation watch adds a
 * third state that is neither: enabled, correctly authored, and standing on a
 * situation the platform does not detect on its chains. It will never fire, and
 * showing it as Active is the specific lie this state exists to stop telling.
 *
 * Paused outranks uncovered — a user who switched a watch off is told what they
 * did, not what the platform cannot do.
 *
 * A fourth state sits between covered and not: the only thing detecting this is
 * one of the user's own pipelines, which runs when they start a run. It does
 * fire, so "Not covered" would be wrong; it does not fire on its own, so
 * "Active" would be the same lie one notch quieter.
 *
 * A FIFTH is `pipeline_id`. A watch bound to one pipeline definition fires only
 * on that pipeline's runs — the detail page says so in as many words ("Only
 * fires on runs of …") — and this read "Active" for it, which is the identical
 * promise to an unbound watch on a platform-swept situation. It is ranked below
 * coverage because a bound watch on a situation nothing detects is not firing
 * for the bigger reason, and above "Active" because "Active" is the claim it
 * cannot support.
 */
export function watchStatus(watch: WatchItem): WatchStatus {
  if (watch.enabled === false) return { label: "Paused", variant: "secondary" };
  if (watch.coverage === "unavailable") {
    return { label: "Not covered", variant: "outline" };
  }
  if (watch.coverage === "available" && watch.covered_by === "tenant") {
    return { label: "On your runs", variant: "outline" };
  }
  // Same wording as the detail page, so the two surfaces say one thing.
  if (watch.pipeline_id) {
    return { label: "Only on its pipeline", variant: "outline" };
  }
  return { label: "Active", variant: "default" };
}

/**
 * The chain a watch is scoped to, or undefined for "every chain".
 *
 * `chain_ids` is the wire field and it is a list; the page only has room for
 * one, and a watch with several is shown as its first plus a count by the
 * caller. Reading `chain` too keeps a locally-built prefill working.
 */
export function watchChain(watch: WatchItem): string | undefined {
  return watch.chain_ids?.[0] ?? watch.chain;
}

/**
 * What the watch's coverage actually means, in the terms of whoever can act.
 *
 * The list shows one word because a list has room for one word. Here the
 * distinction is the whole value: waiting helps in one case, switching a
 * pipeline on helps in another, and editing the watch is the only thing that
 * helps in the third. Coverage supplied by the user's own pipeline gets a line
 * too — it is good news with a condition attached, and the condition is the
 * part that explains an empty alert history.
 */
export function coverageExplanation(watch: WatchItem): string | null {
  if (watch.coverage === "available") {
    return watch.covered_by === "tenant"
      ? "Nothing on the platform sweeps this, but one of your own pipelines " +
          "detects it. That means this watch fires on the blocks your runs " +
          "cover — start a run, or use Scan history, rather than expecting it " +
          "to arrive on its own."
      : null;
  }
  if (watch.coverage !== "unavailable") return null;
  switch (watch.coverage_reason) {
    case "no_pipeline":
      return (
        "The platform can detect this, but no pipeline is running it on the " +
        "chain this watch names. An operator enabling one makes this watch go " +
        "live — no edit needed here."
      );
    case "no_detector":
      return (
        "Nothing on the platform detects this situation yet. The watch is " +
        "saved and starts firing when the detector ships."
      );
    case "unknown_situation":
      return (
        "This watch names a situation the catalog no longer has. It cannot " +
        "fire until it points at one that exists."
      );
    default:
      return "This watch will not fire on the chains it names.";
  }
}

/** How far back "Scan history" reaches by default. */
const SCAN_DAYS = 7;

/**
 * The `/runs` deep link behind "Scan history".
 *
 * A watch only sees what happens after it is created, which is the wrong answer
 * to "show me new Uniswap pools" the first time somebody asks it. The pipeline
 * can be run over a past window on demand — that is a `block_range` run job —
 * so this hands the run dialog the window and the subject rather than making
 * the user reconstruct both.
 *
 * A default window rather than a prompt: the dialog resolves these dates to
 * real blocks and shows the result before anything is submitted, so arriving
 * with a sensible window costs nothing and asking first costs a step.
 */
export function scanSearch(watch: WatchItem): {
  chain?: string;
  from: string;
  to: string;
  address?: string;
} {
  const now = Date.now();
  const chain = watchChain(watch);
  return {
    ...(chain ? { chain } : {}),
    from: new Date(now - SCAN_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    to: new Date(now).toISOString(),
    // An address watch names the contract whose activity it wants, which the
    // run can filter on directly. A situation watch names no address — it is
    // the decoded shape that matters — so its window is scanned in full.
    ...(watch.kind === "address" && watch.target ? { address: watch.target } : {}),
  };
}

/**
 * When the watch last fired, phrased for a row rather than a log line.
 *
 * A watch that has never fired is the common case on a new install and is not
 * an error, so it reads as a plain statement rather than an empty cell the user
 * has to interpret.
 */
export function lastHitLabel(watch: WatchItem): string {
  if (!watch.last_hit_at) return "Never fired";
  return `Last fired ${new Date(watch.last_hit_at * 1000).toLocaleString()}`;
}
